import React, { useContext, useState } from "react";
import { motion } from "framer-motion";
import {
  Heart,
  Briefcase,
  Brain,
  Smile,
  Activity,
  CheckCircle,
  MessageCircle,
  Sparkles,
  ChevronRight,
  Shield,
  Users,
  Target,
  ExternalLink,
} from "lucide-react";
import WellnessGuy from "../../assets/images/wellness1.png";
import WellnessGuy2 from "../../assets/images/wellness2.png";
import WellnessGuyCareer from "../../assets/images/wellness-career.png";
import WellnessGuyStress from "../../assets/images/wellness-stress.png";
import WellnessGuyEmotional from "../../assets/images/wellness-emotional.png";
import WellnessGuyPhysical from "../../assets/images/wellness-physical.png";
import { Redirect } from "react-router-dom";
import { MainStore } from "../../stores/Main";
import { trackChatLinkClicked } from "../../utils/posthogTracking";
import WellnessScoresDashboard from "../../components/wellness-scores-dashboard/wellness-scores-dashboard";
import useWellnessScores from "../../hooks/useWellnessScores";

const wellnessOptions = {
  coach: {
    title: "Wellness Coach",
    description:
      "Based on your answers, you can talk with your personal wellness coach to resolve issues that may otherwise prevent you from achieving your goals.",
    image: WellnessGuy2,
    icon: Heart,
    color: "blue",
  },
  career: {
    title: "Career Growth",
    description:
      "Achieve your professional goals and be more productive in your workplace with your personal assistant.",
    image: WellnessGuyCareer,
    icon: Briefcase,
    color: "purple",
  },
  stress: {
    title: "Stress & Coping",
    description:
      "Take easy steps to cope with everyday stress and forget about worrying for insignificant things. ",
    image: WellnessGuyStress,
    icon: Brain,
    color: "green",
  },
  emotional: {
    title: "Emotional Health",
    description:
      "Cope wisely with your emotions and find the sense of well-being and acknowledgement about your inner self.",
    image: WellnessGuyEmotional,
    icon: Smile,
    color: "orange",
  },
  physical: {
    title: "Physical Health or Fitness",
    description:
      "Find a better way not only to execute daily activities the most productive way, but also  to manage disease, fatigue and sedentary behaviour. ",
    image: WellnessGuyPhysical,
    icon: Activity,
    color: "teal",
  },
};

const WellnessCoachPage = () => {
  const [selectedOption, setSelectedOption] = useState("coach");
  const [showCoach, setShowCoach] = useState(false);
  const { questionnaire, sentimentQuestionnaire } = useContext(MainStore);
  const { data: wellnessData } = useWellnessScores();

  const isReturningUser = wellnessData?.history?.length > 1;

  const colorClasses = {
    blue: "from-blue-500 to-blue-600",
    purple: "from-purple-500 to-purple-600",
    green: "from-green-500 to-green-600",
    orange: "from-orange-500 to-orange-600",
    teal: "from-teal-500 to-teal-600",
  };

  // Wellness coach embed URL. The coach is rendered inline as an iframe by
  // default; openPopup() below is kept as a fallback for when the embedded
  // (third-party) session can't authenticate. embed=1 makes the coach app's
  // background transparent so the platform gradient shows through seamlessly
  // — the popup deliberately omits it (it needs its own background).
  const coachSrc = `${process.env.REACT_APP_WELLNESSCOACH_URL}?mode=web&embed=1&openerHost=${window.location.host}`;

  // Reveal the inline coach iframe and track the launch.
  const startCoach = () => {
    trackChatLinkClicked("wellness_coach", selectedOption);
    setShowCoach(true);
  };

  const openPopup = (mode) => {
    // Track chat link clicked from wellness coach
    trackChatLinkClicked("wellness_coach", selectedOption);
    
    const width = 700;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popupWindow = window.open(
      process.env.REACT_APP_WELLNESSCOACH_URL +
        "?mode=" +
        mode +
        "&openerHost=" +
        window.location.host,
      "WellnessCoach",
      `width=${width},height=${height},top=${top},left=${left}`
    );

    if (!popupWindow) {
      alert(
        "Popup was blocked by the browser. Please disable the popup blocker and try again."
      );
      return;
    }

    popupWindow.focus();
  };

  if (!questionnaire) {
    return (
      <Redirect
        to={{
          pathname: "/questionnaire",
          state: { from: "/wellness-coach" },
        }}
      />
    );
  }

  if (!sentimentQuestionnaire) {
    return (
      <Redirect
        to={{
          pathname: "/sentiment-questionnaire",
          state: { from: "/wellness-coach" },
        }}
      />
    );
  }

  return (
    // No page background: the layout's shared gradient shows, matching
    // every other page.
    <div className="min-h-full overflow-x-hidden">
      {/* Background orbs */}
      <div className="absolute inset-0">
        <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="absolute top-40 left-0 h-80 w-80 rounded-full bg-purple-600/20 blur-3xl" />
        <div className="absolute bottom-40 right-20 h-80 w-80 rounded-full bg-teal-500/20 blur-3xl" />
      </div>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 py-12">
          {/* Inline Wellness Coach */}
          {showCoach && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12"
            >
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={() => setShowCoach(false)}
                  className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
                >
                  <ChevronRight size={16} className="rotate-180" />
                  Back to overview
                </button>
                <button
                  type="button"
                  onClick={() => openPopup("web")}
                  className="inline-flex items-center gap-2 text-sm text-blue-300 hover:text-blue-200 transition-colors"
                >
                  <ExternalLink size={16} />
                  Open in new window
                </button>
              </div>
              <iframe
                src={coachSrc}
                title="Wellness Coach"
                allow="microphone"
                className="w-full h-[calc(100dvh-11rem)] min-h-[420px] sm:h-[calc(100dvh-16rem)] max-h-[900px]"
              />
              <p className="mt-3 text-gray-500 text-sm text-center">
                Trouble loading the coach? Use "Open in new window" to continue
                in a separate tab.
              </p>
            </motion.div>
          )}

          {/* Overview content hides while the coach is open — the chat IS
              the page then; "Back to overview" restores it. */}
          {!showCoach && (
          <>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {isReturningUser ? (
                <>
                  Welcome back to your{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-500">
                    Wellness Journey
                  </span>
                </>
              ) : (
                <>
                  Congratulations on completing the{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-500">
                    Personal Sentiment Survey!
                  </span>
                </>
              )}
            </h1>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="order-2 lg:order-1"
            >
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
                <p className="text-gray-300 text-lg mb-6">
                  {isReturningUser
                    ? "Your wellness scores have been updated. Chat with your coach to review your progress and get personalized guidance."
                    : "You've taken an important first step by providing Freede with valuable information needed to better understand you."}
                </p>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={startCoach}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all"
                >
                  <MessageCircle size={20} />
                  Chat Now
                  <ChevronRight size={20} />
                </motion.button>
                
                <div className="mt-6 flex items-center gap-4 text-sm text-gray-400">
                  <div className="flex items-center gap-2">
                    <Shield size={16} className="text-green-400" />
                    <span>Secure & Confidential</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users size={16} className="text-blue-400" />
                    <span>Personal Coach</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="order-1 lg:order-2 flex justify-center"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-600/20 rounded-full blur-2xl" />
                <img
                  src={WellnessGuy}
                  alt="Wellness Coach"
                  className="relative w-full max-w-md h-auto"
                />
              </div>
            </motion.div>
          </div>
          </>
          )}
        </div>
      </div>

      {!showCoach && (
      <>
      {/* Wellness Scores Dashboard */}
      <WellnessScoresDashboard />

      {/* Steps Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Chat with your personal <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-500">wellness coach</span>
          </h2>
          <p className="text-gray-400">
            Your Wellness Coach can help identify opportunities for personal
            development and help you achieve your goals.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              number: "1",
              title: "Personal coach",
              description: "Access your own personal smart coach that will use artificial intelligence to engage with you.",
              icon: Users,
            },
            {
              number: "2",
              title: "Understand your issues",
              description: "This will help us better understand your unique set of circumstances.",
              icon: Target,
            },
            {
              number: "3",
              title: "Your best financial future",
              description: "This will provide you advice that can help you achieve your best financial future and the outcomes you desire.",
              icon: CheckCircle,
            },
          ].map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="relative"
            >
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 h-full hover:border-gray-600 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-xl">
                    {step.number}
                  </div>
                  <step.icon size={24} className="text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-gray-400 text-sm">{step.description}</p>
              </div>
              {index < 2 && (
                <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                  <ChevronRight size={24} className="text-gray-600" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
      {/* Wellness Options Section */}
      <div className="bg-gray-800/30 backdrop-blur-sm border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Change Your Life with your <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-500">Wellness Coach</span>
            </h2>
            <p className="text-gray-400">
              Customized Coaching, that provides advice and personal life changes that
              you can use to enhance your life.
            </p>
          </motion.div>

          {/* Selected Option Display */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            key={selectedOption}
            className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 mb-8"
          >
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-3 mb-4">
                  {(() => {
                    const Icon = wellnessOptions[selectedOption].icon;
                    return (
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${colorClasses[wellnessOptions[selectedOption].color]} flex items-center justify-center`}>
                        <Icon size={28} className="text-white" />
                      </div>
                    );
                  })()}
                  <h3 className="text-2xl font-semibold text-white">
                    {wellnessOptions[selectedOption].title}
                  </h3>
                </div>
                <p className="text-gray-300 text-lg">
                  {wellnessOptions[selectedOption].description}
                </p>
              </div>
              <div className="order-1 lg:order-2 flex justify-center">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-600/10 rounded-full blur-2xl" />
                  <img
                    src={wellnessOptions[selectedOption].image}
                    alt={wellnessOptions[selectedOption].title}
                    className="relative w-72 h-72 object-contain"
                  />
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Wellness Option Buttons */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(wellnessOptions).map(([key, option], index) => {
              const Icon = option.icon;
              return (
                <motion.button
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedOption(key)}
                  className={`
                    relative p-4 rounded-xl transition-all
                    ${
                      selectedOption === key
                        ? `bg-gradient-to-r ${colorClasses[option.color]} text-white shadow-lg`
                        : "bg-gray-800/50 hover:bg-gray-700/50 text-gray-300 border border-gray-700"
                    }
                  `}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Icon size={24} />
                    <span className="text-sm font-medium text-center">
                      {option.title.split(" ").slice(0, 2).join(" ")}
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={startCoach}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all"
            >
              <Sparkles size={20} />
              Continue Your Journey
              <ChevronRight size={20} />
            </motion.button>

            <p className="mt-4 text-gray-500 text-sm">
              Your personal coach is ready to help you reach your goals
            </p>
          </motion.div>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default WellnessCoachPage;
